import { useEffect, useState } from "react";
import { api, fmt, formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const STATUS_COLORS = {
  new: "bg-zinc-500/20 text-zinc-300",
  awaiting_deposit: "bg-yellow-500/15 text-yellow-400",
  deposit_paid: "bg-blue-500/15 text-blue-400",
  approved: "bg-brand/15 text-brand",
  balance_due: "bg-orange-500/15 text-orange-400",
  completed: "bg-green-500/15 text-green-400",
  declined: "bg-red-500/15 text-red-400",
  pending: "bg-yellow-500/15 text-yellow-400",
  paid: "bg-green-500/15 text-green-400",
};

const Badge = ({ status }) => (
  <span data-testid={`status-badge-${status}`} className={`text-[10px] uppercase tracking-widest px-2.5 py-1 ${STATUS_COLORS[status] || "bg-white/10 text-zinc-300"}`}>
    {status.replace(/_/g, " ")}
  </span>
);

export default function OrdersAdmin() {
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [linkFor, setLinkFor] = useState(null);
  const [linkAmount, setLinkAmount] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    Promise.all([api.get("/admin/bookings"), api.get("/admin/orders")])
      .then(([b, o]) => { setBookings(b.data); setOrders(o.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const act = async (path, successMsg) => {
    try {
      const { data } = await api.post(path);
      toast.success(data.status === "charged" ? "Balance charged to card on file."
        : data.payment_url ? `Balance link emailed to client.` : successMsg);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendLink = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/bookings/${linkFor.id}/payment-link`, { amount: Number(linkAmount), label: `Booking payment — ${linkFor.type}` });
      toast.success("Payment link emailed to client.");
      setLinkFor(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-display text-4xl text-white mb-8">Orders & Bookings</h1>
      <Tabs defaultValue="bookings">
        <TabsList className="bg-[#0A0A0A] border border-white/10 mb-6">
          <TabsTrigger data-testid="tab-bookings" value="bookings">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-marketing-orders" value="orders">Marketing Orders ({orders.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings">
          {loading ? <p className="text-zinc-500">Loading…</p> : bookings.length === 0 ? (
            <p className="text-zinc-500 text-sm">No bookings yet.</p>
          ) : (
            <div className="space-y-4">
              {bookings.map((b) => (
                <div key={b.id} data-testid={`booking-row-${b.id}`} className="border border-white/10 bg-[#0A0A0A] p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-white font-medium">{b.client?.name}</h3>
                        <Badge status={b.status} />
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">
                        {b.type.toUpperCase()} · {b.listing_title || b.details?.job_type || "General request"} · {b.details?.date || "No date"} · {b.details?.hours}h
                        {b.type === "model" ? ` · ${b.details?.num_models} models` : ""}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">{b.client?.email} · {b.client?.phone} · IG: {b.client?.ig}</p>
                      {b.details?.location && <p className="text-xs text-zinc-500 mt-1">Location: {b.details.location}</p>}
                      {b.details?.notes && <p className="text-xs text-zinc-400 mt-2">"{b.details.notes}"</p>}
                    </div>
                    <div className="text-right">
                      <p className="font-display text-2xl text-white">{fmt(b.estimate)}</p>
                      <p className="text-xs text-zinc-500">Deposit {fmt(b.deposit_amount)} · Balance {fmt(b.balance_amount)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/5">
                    {["deposit_paid", "new"].includes(b.status) && (
                      <button data-testid={`approve-${b.id}`} onClick={() => act(`/admin/bookings/${b.id}/approve`, "Booking approved.")}
                        className="text-xs font-semibold tracking-widest uppercase bg-brand hover:bg-brand-hover text-white px-4 py-2 transition-colors">Approve</button>
                    )}
                    {["deposit_paid", "approved"].includes(b.status) && b.balance_amount > 0 && (
                      <button data-testid={`charge-balance-${b.id}`} onClick={() => act(`/admin/bookings/${b.id}/charge-balance`, "Balance requested.")}
                        className="text-xs font-semibold tracking-widest uppercase bg-green-600 hover:bg-green-700 text-white px-4 py-2 transition-colors">Charge Balance</button>
                    )}
                    {!["completed", "declined"].includes(b.status) && (
                      <button data-testid={`send-link-${b.id}`} onClick={() => { setLinkFor(b); setLinkAmount(b.balance_amount || ""); }}
                        className="text-xs font-semibold tracking-widest uppercase border border-white/20 hover:border-brand text-zinc-300 px-4 py-2 transition-colors">Payment Link</button>
                    )}
                    {b.status === "balance_due" && (
                      <button data-testid={`complete-${b.id}`} onClick={() => act(`/admin/bookings/${b.id}/complete`, "Marked completed.")}
                        className="text-xs font-semibold tracking-widest uppercase border border-white/20 hover:border-green-500 text-zinc-300 px-4 py-2 transition-colors">Mark Completed</button>
                    )}
                    {!["completed", "declined"].includes(b.status) && (
                      <button data-testid={`decline-${b.id}`} onClick={() => act(`/admin/bookings/${b.id}/decline`, "Booking declined.")}
                        className="text-xs font-semibold tracking-widest uppercase text-red-400 hover:text-red-300 px-4 py-2 transition-colors">Decline</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders">
          {loading ? <p className="text-zinc-500">Loading…</p> : orders.length === 0 ? (
            <p className="text-zinc-500 text-sm">No marketing orders yet.</p>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} data-testid={`order-row-${o.id}`} className="border border-white/10 bg-[#0A0A0A] p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-white font-medium">{o.client?.artist}</h3>
                        <Badge status={o.status} />
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">{o.client?.email} · {o.client?.phone}</p>
                      {o.client?.song_link && <p className="text-xs text-zinc-500 mt-1 truncate max-w-md">Song: {o.client.song_link}</p>}
                      <ul className="mt-2 space-y-1">
                        {o.items?.map((i, idx) => (
                          <li key={idx} className="text-xs text-zinc-400">
                            {i.name} — {fmt(i.charged)}{i.checkout_type === "deposit_approval" ? " (deposit)" : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-2xl text-white">{fmt(o.total)}</p>
                      {o.balance_due > 0 && <p className="text-xs text-orange-400">Balance due {fmt(o.balance_due)}</p>}
                      <p className="text-xs text-zinc-600 mt-1">{new Date(o.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/5">
                    {o.status === "deposit_paid" && (
                      <button data-testid={`approve-order-${o.id}`} onClick={() => act(`/admin/orders/${o.id}/approve`, "Order approved.")}
                        className="text-xs font-semibold tracking-widest uppercase bg-brand hover:bg-brand-hover text-white px-4 py-2 transition-colors">
                        Approve & Request Balance
                      </button>
                    )}
                    {["paid", "balance_due"].includes(o.status) && (
                      <button data-testid={`complete-order-${o.id}`} onClick={() => act(`/admin/orders/${o.id}/complete`, "Order completed.")}
                        className="text-xs font-semibold tracking-widest uppercase border border-white/20 hover:border-green-500 text-zinc-300 px-4 py-2 transition-colors">
                        Mark Completed
                      </button>
                    )}
                    {o.status === "pending" && <span className="text-xs text-zinc-500 py-2">Awaiting payment (abandoned cart reminder will be emailed automatically)</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!linkFor} onOpenChange={() => setLinkFor(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-zinc-100 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">Send Payment Link</DialogTitle>
          </DialogHeader>
          <form onSubmit={sendLink} className="space-y-4">
            <p className="text-xs text-zinc-500">A Stripe checkout link will be emailed to {linkFor?.client?.email}.</p>
            <div>
              <Label className="text-xs text-zinc-400">Amount (USD)</Label>
              <Input data-testid="payment-link-amount" type="number" min="1" step="0.01" required
                value={linkAmount} onChange={(e) => setLinkAmount(e.target.value)}
                className="bg-white/5 border-white/10 mt-1" />
            </div>
            <button data-testid="payment-link-send-btn" type="submit"
              className="w-full bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase py-3 transition-colors">
              Email Payment Link
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
